import {Metadata, SEOPositionable} from '@/modules/seo/types'
import KeyValuePair from '@/modules/core/support/KeyValuePair';
import Gallery from '@/modules/cms/modules/gallery/model/Gallery';
import User from '../user/model/User';


/**
 * Puntero a un recurso de contenido del sistema CMS
 */
export interface ResourcePointer {
    resourceType: string
    resourceId: string
}

/**
 * Página
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class Page implements SEOPositionable {
    public id = 0
    public route = ''
    public createdAt = 0
    public updatedAt = 0
    public screenshotPath = ''
    public seo = new Metadata()
    public translations: PageTranslation[] = []
    public isInternal = false
    public isStatic = false
    public gallery = new Gallery()
    public isPrivate = false
    public creator: User | null = null

    /**
     * Propiedad del sistema de moderación que indica si los usuario moderadores
     * han aprobado la página
     */
    public _approved = false

    public embeddedContentType = ''
    public embeddedContent = ''

    /**
     * Indica si la página aún no ha sido insertada en el servidor
     */
    get isNew () {
        return !this.id
    }

    /**
     * Comprueba si la página tiene captura de pantalla
     *
     * @returns {boolean}
     */
    get hasScreenshot (): boolean {
        return this.screenshotPath !== ''
    }

    public set approved (val: boolean) {
        this._approved = val
    }

    public get approved () {
        return this.isInternal ? true : this._approved
    }

    /**
     * Traducción de la página
     *
     * Retorna una traducción registrada en el página o una nueva si no existe
     *
     * @param {string} locale
     * @returns {PageTranslation}
     */
    public translate (locale: string): PageTranslation {
        let translation = this.translations.find(t => t.locale === locale)
        if (translation) {
            return translation
        }

        translation = new PageTranslation(locale)
        this.translations.push(translation)

        return translation
    }

    /**
     * Vuelca la información JSON en una entidad Page
     *
     * @param data
     */
    public static deserialize (data: {[key: string]: any}): Page {
        const page = new Page()
        page.id = data.id
        page.route = data.route
        page.createdAt = parseFloat(data.created_at)
        page.updatedAt = parseFloat(data.updated_at)
        page.screenshotPath = data.screenshot_path
        page.seo = Metadata.deserialize(data.seo)
        page.isInternal = data.is_internal || false
        page.isStatic = data.is_static || false
        page.isPrivate = data.private || false
        page.embeddedContentType = data.embedded_content_type
        page.embeddedContent = data.embedded_content
        page.approved = data.approved || false

        const creator = data.creator || null
        if (creator) {
            page.creator = User.deserialize(creator)
        }

        if (data.gallery) {
            page.gallery = Gallery.deserialize(data.gallery)
        }

        const translations = data.translations
        if (!Array.isArray(translations)) {
            throw new TypeError('data.translations debe ser un array')
        }

        page.translations = translations.map(t => PageTranslation.deserialize(t))

        return page
    }

    /**
     * Serializa la entidad
     */
    public serialize () {
        return {
            embedded_content_type: this.embeddedContentType,
            embedded_content: this.embeddedContent,
            seo: this.seo.serialize(),
            gallery: this.gallery.id,
            translations: this.translations.map(t => t.serialize()),
            private: this.isPrivate,
        }
    }

    get title (): string {
        const translation = this.seo.translate('es')
        if (!translation) {
            return ''
        }

        return translation.title
    }

    /**
     * Retorna el tipo de página
     */
    public get type (): string {
        switch (true) {
            case this.isInternal:
                return 'internal'
            case this.isStatic:
                return 'static'
            default:
                return 'other'
        }
    }
}

/**
 * Traducción de una página
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class PageTranslation {
    public id = ''
    constructor (public locale: string, public content = '') {}

    /**
     * Convierte un objeto json plano en una entidad
     *
     * @param data { KeyValuePair }
     */
    public static deserialize (data: KeyValuePair) {
        const translation = new this(data.locale, data.content)
        translation.id = data.id

        return translation
    }

    public serialize () {
        return {
            locale: this.locale,
            content: this.content,
        }
    }
}
